import { AlertTriangleIcon } from 'lucide-react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { PopoverCombobox } from '@/components/ui/custom/PopoverCombobox';
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { WaitingSoundName } from '@/hooks/useWaitingSounds';
import { CartesiaEmotion } from '@/shared/types/voiceProviders';

import PronunciationDictionaryToggle from './PronunciationDictionaryToggle';
import { TTSCardProps } from './types';
import { useTTSVoiceOptions } from './useTTSVoiceOptions';
import WaitingSoundsSelector from './WaitingSoundsSelector';

/**
 * Cartesia Voice configuration component
 *
 * Provides form fields for:
 * - Model selection (dropdown)
 * - Voice selection (dropdown)
 * - Volume slider (0-2)
 * - Speed slider (0-2)
 * - Emotion selection (dropdown)
 */
const CartesiaVoice = ({ basePath = 'config.tts_cartesia', strategy }: TTSCardProps) => {
  const { t } = useTranslation();
  const { control, setValue } = useFormContext();

  const emotionOptions: { value: CartesiaEmotion; label: string }[] = [
    { value: 'neutral', label: t('integrations.voice_common.voice.emotion_neutral') },
    { value: 'calm', label: t('integrations.voice_common.voice.emotion_calm') },
    { value: 'content', label: t('integrations.voice_common.voice.emotion_content') },
  ];

  const thinkingSoundName = useWatch({ control, name: 'config.thinking_sound.name' });

  const { voiceOptions, selectVoice } = useTTSVoiceOptions('cartesia', { basePath, strategy });

  return (
    <div className="space-y-6">
      {/* Voice Selector */}
      {voiceOptions.length > 0 ? (
        <FormField
          control={control}
          name={`${basePath}.voice_id`}
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('integrations.voice_common.voice.voice_id')}</FormLabel>
              <FormControl>
                <PopoverCombobox
                  options={voiceOptions}
                  value={field.value || ''}
                  onChange={selectVoice}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      ) : (
        <div className="flex items-center gap-2 text-sm text-destructive">
          <AlertTriangleIcon size={16} />
          {t('integrations.voice_common.voice.no_voices_found')}
        </div>
      )}

      {/* Volume Slider */}
      <FormField
        control={control}
        name={`${basePath}.volume`}
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t('integrations.voice_common.voice.volume')}</FormLabel>
            <FormControl>
              <Slider
                tooltip
                displayRealValue
                min={0}
                max={2}
                step={0.1}
                minLabel={t('integrations.voice_common.voice.lower')}
                maxLabel={t('integrations.voice_common.voice.higher')}
                value={[field.value ?? 1.0]}
                onValueChange={(values) =>
                  setValue(`${basePath}.volume`, values[0], { shouldDirty: true })
                }
              />
            </FormControl>
            <FormDescription>
              {t('integrations.voice_common.voice.volume_description')}
            </FormDescription>
            <FormMessage />
          </FormItem>
        )}
      />

      {/* Speed Slider */}
      <FormField
        control={control}
        name={`${basePath}.speed`}
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t('integrations.voice_common.voice.speed')}</FormLabel>
            <FormControl>
              <Slider
                tooltip
                displayRealValue
                min={0}
                max={2}
                step={0.1}
                minLabel={t('integrations.voice_common.voice.slower')}
                maxLabel={t('integrations.voice_common.voice.faster')}
                value={[field.value ?? 1.0]}
                onValueChange={(values) =>
                  setValue(`${basePath}.speed`, values[0], { shouldDirty: true })
                }
              />
            </FormControl>
            <FormDescription>
              {t('integrations.voice_common.voice.speed_description')}
            </FormDescription>
            <FormMessage />
          </FormItem>
        )}
      />

      {/* Emotion Selector */}
      <FormField
        control={control}
        name={`${basePath}.emotion`}
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t('integrations.voice_common.voice.emotion')}</FormLabel>
            <FormControl>
              <Select
                value={field.value || 'neutral'}
                onValueChange={(value: CartesiaEmotion) =>
                  setValue(`${basePath}.emotion`, value, { shouldDirty: true })
                }
              >
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {emotionOptions.map(({ value, label }) => (
                    <SelectItem key={value} value={value}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormControl>
            <FormDescription>
              {t('integrations.voice_common.voice.emotion_description')}
            </FormDescription>
            <FormMessage />
          </FormItem>
        )}
      />

      {/* Dictionaries live in Moveo's provider account, so a customer's key cannot use them */}
      {!strategy?.speechModelId && (
        <PronunciationDictionaryToggle
          provider="cartesia"
          name={`${basePath}.pronunciation_dictionary_id`}
        />
      )}

      {!strategy && (
        <WaitingSoundsSelector
          value={thinkingSoundName as WaitingSoundName | ''}
          onChange={(value) => setValue('config.thinking_sound.name', value, { shouldDirty: true })}
        />
      )}
    </div>
  );
};

export default CartesiaVoice;
